import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Search } from 'lucide-react';

const Integrations = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const integrations = [
    {
      name: 'HubSpot Sales CRM',
      description: 'Push leads to HubSpot automatically and streamline your sales process. Every new lead is synced in real time, so your marketing and sales teams can act faster and smarter.',
      status: 'available',
      logo: 'https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png',
      color: '#FF7A59',
    },
    {
      name: 'Adobe Marketo Engage',
      description: 'Keep your Marketo database updated effortlessly by sending leads straight from your AALIGN instance. Save time, reduce errors, and ensure every lead is captured instantly for follow-up.',
      status: 'available',
      logo: 'https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://business.adobe.com&size=16',
      color: '#5C4C9F',
    },
    {
      name: 'Airtable',
      description: 'Send your leads directly to Airtable with ease. Every new lead on AALIGN is instantly added, keeping your Airtable base up-to-date without any manual entry.',
      status: 'available',
      logo: 'https://www.google.com/s2/favicons?domain=airtable.com',
      color: '#FCBF49',
    },
    {
      name: 'Websand',
      description: 'Sync your AALIGN leads to Websand effortlessly. Every lead is added instantly, so your marketing workflows run smoothly and your team can focus on engagement.',
      status: 'available',
      logo: 'https://www.google.com/s2/favicons?domain=www.websand.co.uk',
      color: '#6366F1',
    },
    {
      name: 'ZeroBounce',
      description: 'Keep your email list healthy by sending leads directly from AALIGN to ZeroBounce. Every lead is checked automatically so your campaigns reach real prospects.',
      status: 'available',
      logo: 'https://www.google.com/s2/favicons?domain=zerobounce.net',
      color: '#00C853',
    },
    {
      name: 'NeverBounce',
      description: 'Send leads directly from AALIGN to NeverBounce to ensure every email is valid. No manual checks, no invalid contacts - just verified leads ready for engagement.',
      status: 'unavailable',
      logo: 'https://www.google.com/s2/favicons?domain=neverbounce.com',
      color: '#00BCD4',
    },
    {
      name: 'Hunter.io',
      description: 'Easily connect AALIGN with Hunter.io to enrich leads with verified emails. Save time, improve accuracy, and reach the right people every time.',
      status: 'available',
      logo: 'https://www.google.com/s2/favicons?domain=Hunter.io',
      color: '#FF6B35',
    },
    {
      name: 'Pipedrive',
      description: 'Seamlessly transfer leads from AALIGN into Pipedrive. Save time on manual entry and let your team focus on nurturing prospects and winning deals.',
      status: 'unavailable',
      logo: 'https://www.google.com/s2/favicons?domain=pipedrive.com',
      color: '#25D366',
    },
    {
      name: 'Insightly',
      description: 'Effortlessly sync leads to Insightly from your AALIGN instance. Keep contacts organized, pipelines clean, and your sales process running smoothly.',
      status: 'unavailable',
      logo: 'https://www.google.com/s2/favicons?domain=insightly.com',
      color: '#2196F3',
    },
  ];

  const filteredIntegrations = integrations.filter((integration) =>
    integration.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name) => {
    return name.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto">
          <p className="text-orange-600 font-semibold mb-2 uppercase tracking-wide">Integrations</p>
          <h1 className="text-4xl lg:text-5xl font-black mb-6">API Library</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Connect AALIGN with your favorite tools and automate your lead management workflow.
          </p>
        </div>
      </section>

      {/* Search and Integrations */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Search Bar */}
          <div className="relative max-w-md mb-12">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          {/* Integrations Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIntegrations.map((integration, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                {/* Logo and Name */}
                <div className="flex items-start gap-4 mb-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                    style={{ backgroundColor: integration.color }}
                  >
                    {integration.logo ? (
                      <img 
                        src={integration.logo} 
                        alt={integration.name} 
                        className="w-8 h-8 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = getInitials(integration.name);
                        }}
                      />
                    ) : (
                      getInitials(integration.name)
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{integration.name}</h3>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed flex-grow mb-6">
                  {integration.description}
                </p>

                {/* Action Button */}
                <div className="flex justify-end">
                  {integration.status === 'available' ? (
                    <span className="px-6 py-2 text-orange-500 font-semibold">
                      Available
                    </span>
                  ) : (
                    <span className="px-6 py-2 text-gray-400 font-medium">
                      Currently Unavailable
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <p className="text-sm text-gray-500 mt-12 text-center">
            * Logos displayed on this page are the trademarks and copyrighted property of their respective owners. They are used here for identification purposes only.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Integrations;
